/**
 * Shared helpers for AI-suggested field indicators in import / enhance review.
 */

export type AiSuggestedField =
  | 'category'
  | 'vendor'
  | 'description'
  | 'specifications'
  | 'group_label'
  | 'parentComponent';

export type AiSuggestedFields = Partial<Record<AiSuggestedField, boolean>>;

/** High-confidence threshold: auto-populate into editable fields. */
export const AI_HIGH_CONFIDENCE = 0.8;

/** Below this, leave optional fields empty rather than inventing. */
export const AI_LOW_CONFIDENCE = 0.55;

export function clearAiSuggestion(
  suggested: AiSuggestedFields | undefined,
  field: AiSuggestedField
): AiSuggestedFields | undefined {
  if (!suggested?.[field]) return suggested;
  const next = { ...suggested };
  delete next[field];
  return Object.keys(next).length > 0 ? next : undefined;
}

export function hasAnyAiSuggestion(suggested?: AiSuggestedFields): boolean {
  return Boolean(suggested && Object.values(suggested).some(Boolean));
}

/**
 * Mark fields populated by analyze/AI as suggested so the review UI can show badges.
 * Does not invent values — only flags what is already present.
 */
export function markAiSuggestedFields<T extends {
  category?: string;
  vendor?: string;
  description?: string;
  specifications?: unknown;
  group_label?: string;
  parentComponent?: string;
  confidence?: number;
  category_match_confidence?: number;
  source?: string;
  ai_suggested?: AiSuggestedFields;
}>(mod: T, options?: { force?: boolean }): T & { ai_suggested?: AiSuggestedFields } {
  if (mod.ai_suggested && !options?.force) {
    return mod;
  }

  const confidence = typeof mod.confidence === 'number' ? mod.confidence : 0.5;
  const categoryConfidence =
    typeof mod.category_match_confidence === 'number' ? mod.category_match_confidence : confidence;
  const suggested: AiSuggestedFields = {};

  const category = (mod.category || '').trim();
  if (
    category &&
    category !== 'Miscellaneous' &&
    (confidence >= AI_LOW_CONFIDENCE || categoryConfidence >= 0.72)
  ) {
    suggested.category = true;
  }

  if ((mod.vendor || '').trim() && confidence >= AI_LOW_CONFIDENCE) {
    suggested.vendor = true;
  }

  if ((mod.description || '').trim() && confidence >= AI_HIGH_CONFIDENCE) {
    suggested.description = true;
  }

  const specs = mod.specifications;
  const hasSpecs =
    (typeof specs === 'string' && specs.trim() !== '') ||
    (Array.isArray(specs) && specs.length > 0) ||
    (specs && typeof specs === 'object' && Object.keys(specs as object).length > 0);
  if (hasSpecs && confidence >= AI_LOW_CONFIDENCE) {
    suggested.specifications = true;
  }

  if ((mod.group_label || mod.parentComponent || '').trim()) {
    suggested.group_label = true;
  }

  return {
    ...mod,
    ai_suggested: Object.keys(suggested).length > 0 ? suggested : undefined,
  };
}

/** Normalize free-form specs into Key: Value lines when possible. */
export function normalizeSpecLines(specs: unknown): string[] {
  if (!specs) return [];
  if (typeof specs === 'string') {
    return specs
      .split(/\n|;/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (Array.isArray(specs)) {
    return specs.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof specs === 'object') {
    return Object.entries(specs as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${v}`)
      .filter((line) => !line.endsWith(': '));
  }
  return [];
}

export function specsEqual(a: unknown, b: unknown): boolean {
  const left = normalizeSpecLines(a).join('\n');
  const right = normalizeSpecLines(b).join('\n');
  return left === right;
}
